// Complete merged useCaseContent.js file

import React from 'react';
import { 
  Code, 
  Repeat, 
  Share2, 
  MousePointerClick, 
  Users, 
  BarChart, 
  Zap, 
  Database, 
  Palette,
  ArrowRight,
  Sparkles,
  FileText,
  TrendingUp,
  LineChart
} from 'lucide-react';

// Content for organization benefit modals
const useCaseContent = {
  'inclusive': {
    title: 'Make Design More Inclusive',
    content: (
      <div className="space-y-8">
        <div className="bg-yellow-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <Users className="w-6 h-6 text-yellow-500 mr-2" />
            Increase Inclusivity in Your Design Process
          </h3>
          <p className="text-gray-700 mb-6">
            By allowing non-designers to quickly produce visual mockups, your organization encourages cross-team participation. 
            This ensures that every department (marketing, product, customer success) can have direct input, 
            making the entire design process more transparent and inclusive.
          </p>
          
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 border border-yellow-200 bg-white p-4 rounded-lg">
              <h4 className="font-medium text-yellow-700 mb-2">Before dessino.ai</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✕</span>
                  <span>Non-designers rely on rough sketches or verbal descriptions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✕</span>
                  <span>Ideas get lost in translation or misinterpreted</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✕</span>
                  <span>Long wait times for design team availability</span>
                </li>
              </ul>
            </div>
            
            <div className="flex-1 border border-green-200 bg-white p-4 rounded-lg">
              <h4 className="font-medium text-green-700 mb-2">With dessino.ai</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Anyone can create professional-looking mockups</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Clear visual communication of ideas and requirements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Rapid iteration without design team bottlenecks</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h4 className="font-semibold text-lg mb-4 flex items-center">
              <Palette className="w-5 h-5 text-yellow-500 mr-2" />
              Express Ideas Visually
            </h4>
            <p className="text-gray-600 mb-4">
              Simply describe what you want, and watch as your ideas transform into professional UI designs.
              Use natural language to specify layouts, components, colors, and interactions.
            </p>
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-sm text-gray-500 font-medium mb-2">Example Prompt:</p>
              <p className="text-gray-700 italic">"Create a dashboard with a welcome section at the top, 3 metric cards below it, and a data table at the bottom with pagination."</p>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h4 className="font-semibold text-lg mb-4 flex items-center">
              <Share2 className="w-5 h-5 text-yellow-500 mr-2" />
              Democratize Design Decision-Making
            </h4>
            <p className="text-gray-600 mb-4">
              Enable everyone in your organization to contribute visual ideas regardless of their design background.
              Break down departmental silos and improve cross-functional collaboration.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium mb-1">Marketing</p>
                <p className="text-xs text-gray-600">Create landing pages and email templates</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium mb-1">Product</p>
                <p className="text-xs text-gray-600">Design new features and flows</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium mb-1">Sales</p>
                <p className="text-xs text-gray-600">Build custom demo mockups</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium mb-1">Customer Success</p>
                <p className="text-xs text-gray-600">Visualize client-specific solutions</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4">Boost Team Productivity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h4 className="font-medium mb-3">Eliminate Design Bottlenecks</h4>
              <p className="text-gray-600 mb-4">
                Stop waiting for design resources and move projects forward faster when everyone can contribute.
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-yellow-50 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-yellow-500" />
                </div>
                <p className="text-sm text-gray-600">
                  Generate more design ideas by visually engaging your entire team.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h4 className="font-medium mb-3">Resource Optimization</h4>
              <p className="text-gray-600 mb-4">
                Repurpose specialized design resources for complex work while empowering teams to handle routine design tasks.
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                  <LineChart className="w-6 h-6 text-green-500" />
                </div>
                <p className="text-sm text-gray-600">
                  Save design time on routine UI creation tasks while improving quality
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  
  'research': {
    title: 'Test Realistic Interactions',
    content: (
      <div className="space-y-8">
        <div className="bg-orange-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <MousePointerClick className="w-6 h-6 text-orange-500 mr-2" />
            Beyond Static Mockups
          </h3>
          <p className="text-gray-700 mb-6">
            With dynamic scripts, modals, and click-based data changes built into your prototypes, you can simulate real user flows in user tests. 
            Observing genuine user reactions to interactive elements helps validate concepts faster and more effectively than static wireframes alone.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                <div className="relative">
                  <div className="w-6 h-6 bg-orange-200 rounded"></div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-300 rounded-full flex items-center justify-center text-xs text-white">3</div>
                </div>
              </div>
              <h4 className="font-medium mb-2">Modal Windows</h4>
              <p className="text-sm text-gray-600">Create prototypes with working modals that open and close, showing additional content or forms</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                <div className="space-y-1">
                  <div className="w-6 h-1 bg-orange-300 rounded"></div>
                  <div className="w-6 h-1 bg-orange-200 rounded"></div>
                  <div className="w-6 h-1 bg-orange-200 rounded"></div>
                </div>
              </div>
              <h4 className="font-medium mb-2">Tabbed Interfaces</h4>
              <p className="text-sm text-gray-600">Build prototypes with working tabs that switch between different content sections</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                <BarChart className="w-6 h-6 text-orange-400" />
              </div>
              <h4 className="font-medium mb-2">Dynamic Content</h4>
              <p className="text-sm text-gray-600">Show different data based on user selections, filtering, and interactions</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h4 className="font-semibold text-lg mb-4 flex items-center">
              <Zap className="w-5 h-5 text-orange-500 mr-2" />
              Real User Testing
            </h4>
            <p className="text-gray-600 mb-4">
              Test your concepts with real users who can click, type, and interact with your prototype.
              Get authentic feedback on usability before committing to development.
            </p>
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-sm font-medium mb-2">Interaction Examples:</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Form validation with error messages</li>
                <li>• Multi-step processes and wizards</li>
                <li>• Dropdown menus and navigation</li>
                <li>• Filter and sort functionality</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h4 className="font-semibold text-lg mb-4 flex items-center">
              <Repeat className="w-5 h-5 text-orange-500 mr-2" />
              Rapid Iteration
            </h4>
            <p className="text-gray-600 mb-4">
              Quickly adjust and refine your interactive prototype based on feedback.
              Modify behaviors, add new interactions, or completely reimagine flows in minutes.
            </p>
            <div className="bg-gray-50 p-4 rounded border flex flex-col sm:flex-row items-center justify-between">
              <div className="text-sm mb-3 sm:mb-0">
                <p className="font-medium">Traditional Process:</p>
                <p className="text-gray-600">Days to implement changes</p>
              </div>
              <ArrowRight className="text-gray-400 transform rotate-90 sm:rotate-0 my-2 sm:my-0" />
              <div className="text-sm">
                <p className="font-medium">With dessino.ai:</p>
                <p className="text-gray-600">Minutes to implement changes</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border border-orange-200 rounded-lg overflow-hidden">
          <div className="bg-orange-50 p-4 border-b border-orange-200">
            <h4 className="font-semibold">Case Study: E-commerce Checkout Flow</h4>
          </div>
          <div className="p-6 bg-white">
            <p className="text-gray-600 mb-6">
              A UX team needed to test different checkout processes to reduce cart abandonment.
              Using dessino.ai, they created fully interactive prototypes with:
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="border rounded p-3 text-center">
                <div className="text-2xl mb-2">1️⃣</div>
                <p className="text-sm">Working form validation</p>
              </div>
              <div className="border rounded p-3 text-center">
                <div className="text-2xl mb-2">2️⃣</div>
                <p className="text-sm">Address auto-complete</p>
              </div>
              <div className="border rounded p-3 text-center">
                <div className="text-2xl mb-2">3️⃣</div>
                <p className="text-sm">Payment method toggling</p>
              </div>
            </div>
            
            <p className="text-gray-600">
              They tested with real users, gathered specific feedback on each interaction,
              and refined the flow before any development began—saving weeks of coding time.
            </p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4">Comparison: Static vs. Interactive Testing</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded border">
              <h4 className="font-medium text-sm mb-3">Static Prototypes</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✕</span>
                  <span>Limited to visual design only</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✕</span>
                  <span>Requires verbal explanation of interactions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✕</span>
                  <span>Tests only hypothetical user flows</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✕</span>
                  <span>Misses critical interaction feedback</span>
                </li>
              </ul>
            </div>
            <div className="bg-orange-50 p-4 rounded border border-orange-100">
              <h4 className="font-medium text-sm mb-3">Interactive Prototypes</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Functional modals, dropdowns and popovers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Realistic data interactions and state changes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Tests complete, working user flows</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Gathers authentic interaction feedback</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  },
  
  'development': {
    title: 'Speed Up Development',
    content: (
      <div className="space-y-8">
        <div className="bg-blue-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <Code className="w-6 h-6 text-blue-500 mr-2" />
            Streamlined Design-to-Code Process
          </h3>
          <p className="text-gray-700 mb-6">
            Bridge the gap between design and development by generating clean, structured HTML that
            can be fed directly to Large Language Models (LLMs) for coding into your framework of choice.
            Instead of manually explaining each UI element to developers or AI coding assistants, export a functional HTML prototype.
          </p>
          
          {/* Mobile-responsive workflow diagram */}
          <div className="relative py-6">
            {/* On mobile: Stack the flow vertically with connecting lines */}
            <div className="md:hidden flex flex-col items-center">
              <div className="text-center z-10 mb-8">
                <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                  <FileText className="w-8 h-8 text-blue-500" />
                </div>
                <p className="text-sm font-medium">Design</p>
                <div className="h-10 w-0.5 bg-blue-200 mx-auto mt-2"></div>
              </div>
              
              <div className="text-center z-10 mb-8">
                <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                  <Code className="w-8 h-8 text-blue-500" />
                </div>
                <p className="text-sm font-medium">HTML Export</p>
                <div className="h-10 w-0.5 bg-blue-200 mx-auto mt-2"></div>
              </div>
              
              <div className="text-center z-10 mb-8">
                <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                  <Sparkles className="w-8 h-8 text-blue-500" />
                </div>
                <p className="text-sm font-medium">LLM Processing</p>
                <div className="h-10 w-0.5 bg-blue-200 mx-auto mt-2"></div>
              </div>
              
              <div className="text-center z-10">
                <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                  <Database className="w-8 h-8 text-blue-500" />
                </div>
                <p className="text-sm font-medium">Framework Code</p>
              </div>
            </div>
            
            {/* On desktop: Horizontal timeline with connecting line */}
            <div className="hidden md:block">
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-blue-200 transform -translate-y-1/2"></div>
              
              <div className="flex justify-between relative">
                <div className="text-center z-10">
                  <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                    <FileText className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium">Design</p>
                </div>
                
                <div className="text-center z-10">
                  <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                    <Code className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium">HTML Export</p>
                </div>
                
                <div className="text-center z-10">
                  <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                    <Sparkles className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium">LLM Processing</p>
                </div>
                
                <div className="text-center z-10">
                  <div className="w-16 h-16 bg-white rounded-full border-2 border-blue-300 mx-auto flex items-center justify-center mb-2">
                    <Database className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium">Framework Code</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h4 className="font-semibold text-lg mb-4 flex items-center">
              <Code className="w-5 h-5 text-blue-500 mr-2" />
              HTML to Framework Code
            </h4>
            <p className="text-gray-600 mb-4">
              Export clean HTML/CSS and feed it to LLMs to generate framework-specific code.
              Focus development efforts on backend logic rather than UI implementation.
            </p>
            <div className="bg-gray-50 p-4 rounded border space-y-3">
              <div className="flex items-start">
                <div className="w-10 text-xs text-right pr-2 pt-1 text-gray-500 flex-shrink-0">1</div>
                <div className="bg-blue-50 px-3 py-1 rounded flex-grow text-sm font-mono overflow-x-auto">
                  <span className="text-blue-700">{'<div>'}</span>
                  <span className="text-gray-700">{'...'}</span>
                  <span className="text-blue-700">{'</div>'}</span>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 text-xs text-right pr-2 pt-1 text-gray-500 flex-shrink-0">2</div>
                <div className="bg-green-50 px-3 py-1 rounded flex-grow text-sm font-mono overflow-x-auto">
                  <span className="text-green-700">{'<React.Fragment>'}</span>
                  <span className="text-gray-700">{'...'}</span>
                  <span className="text-green-700">{'</React.Fragment>'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h4 className="font-semibold text-lg mb-4 flex items-center">
              <Zap className="w-5 h-5 text-blue-500 mr-2" />
              Focus on Complex Logic
            </h4>
            <p className="text-gray-600 mb-4">
              Let AI handle the UI implementation details while you focus your development
              resources on the complex business logic and unique features of your application.
            </p>
            <div className="rounded overflow-hidden">
              <div className="bg-gray-800 text-white text-xs px-4 py-2">
                Development Time Allocation
              </div>
              <div className="p-4 bg-gray-50 space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Traditional: UI Development</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-3 bg-gray-400 rounded-full" style={{ width: '60%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>With dessino.ai: UI Development</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-3 bg-blue-500 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-blue-200 rounded-lg overflow-hidden">
          <div className="bg-blue-50 p-4 border-b border-blue-200">
            <h4 className="font-semibold">Technical Example</h4>
          </div>
          <div className="p-6">
            <h5 className="font-medium mb-3">From dessino.ai Export to React Component</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">1. Export HTML from dessino.ai</p>
                <div className="bg-gray-800 p-3 rounded text-green-400 text-xs font-mono h-48 overflow-y-auto">
                  {`<div class="card">
  <div class="card-header">
    <h2>User Profile</h2>
  </div>
  <div class="card-body">
    <div class="user-avatar">...</div>
    <div class="user-info">...</div>
  </div>
  <div class="card-footer">
    <button class="btn primary">
      Save Changes
    </button>
  </div>
</div>`}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">2. LLM converts to React</p>
                <div className="bg-gray-800 p-3 rounded text-blue-400 text-xs font-mono h-48 overflow-y-auto">
                  {`import React from 'react';

const UserProfileCard = () => {
  const handleSave = () => {
    // Business logic here
  };
  
  return (
    <div className="card">
      <div className="card-header">
        <h2>User Profile</h2>
      </div>
      <div className="card-body">
        <div className="user-avatar">...</div>
        <div className="user-info">...</div>
      </div>
      <div className="card-footer">
        <button 
          className="btn primary"
          onClick={handleSave}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default UserProfileCard;`}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4">Development Acceleration</h3>
          <div className="flex flex-col md:flex-row md:items-center space-y-6 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="mb-2">
                <span className="text-sm font-medium">Traditional Process</span>
              </div>
              <div className="mb-1 h-3 bg-gray-200 rounded-full">
                <div className="w-full h-3 bg-gray-400 rounded-full"></div>
              </div>
              <div className="grid grid-cols-4 text-xs text-gray-500">
                <span>Design</span>
                <span>Explain</span>
                <span>Code</span>
                <span>Revise</span>
              </div>
            </div>
            <ArrowRight className="text-gray-400 hidden md:block transform rotate-90 md:rotate-0" />
            <div className="flex-1">
              <div className="mb-2">
                <span className="text-sm font-medium">With dessino.ai</span>
              </div>
              <div className="mb-1 h-3 bg-gray-200 rounded-full">
                <div className="w-1/3 h-3 bg-blue-500 rounded-full"></div>
              </div>
              <div className="grid grid-cols-3 text-xs text-gray-500">
                <span>Export</span>
                <span>Integrate</span>
                <span>Ship</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
};

export default useCaseContent;